import { CopyOutlined, ExportOutlined } from '@ant-design/icons'
import { Button, Card, Input, Space, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { DaoClaimHistoryData, DaoClaimHistoryItemType } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import ButtonCopy from 'component/button/button-copy'
import LinkAddress from 'component/link/link-address'
import { DAO_PAGE_SIZE_OPTIONS } from 'constants/dao-voting'
import { renderAddressUrl } from 'utils'
import { formatNumber } from 'utils/formatted-number'
import { logError } from 'utils/logger'

interface ClaimHistoryCardProps {
  /** tokenize (PTO project) id */
  tokenizeId: string
}

const POOL_TYPE_TITLE: Record<string, string> = {
  REFUND: 'Claim Refund History',
  RWA_REWARDS: 'Claim Rewards History',
}

/**
 * Claim Rewards/Refund History (SRS 5.10) - server-side search, sort and
 * paging over GET /admin/dao-voting/pto-projects/:tokenizeId/claim-history.
 * Default order comes from the BE: newest first.
 */
const ClaimHistoryCard = ({ tokenizeId }: ClaimHistoryCardProps) => {
  const [data, setData] = useState<DaoClaimHistoryData | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [sort, setSort] = useState<string | undefined>(undefined)
  const [searchValue, setSearchValue] = useState('')
  const [keyword, setKeyword] = useState('')

  const getClaimHistory = useCallback(async () => {
    try {
      setLoading(true)
      const res = await AppAdmin.getDaoVotingClaimHistory(tokenizeId, {
        page,
        limit,
        search: keyword,
        sort,
      })
      setData(res.data?.data || null)
    } catch (error) {
      logError('Error fetching claim history: ', error)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [tokenizeId, page, limit, keyword, sort])

  useEffect(() => {
    getClaimHistory()
  }, [getClaimHistory])

  const onSearch = () => {
    setPage(1)
    setKeyword(searchValue.trim())
  }

  const columns: ColumnsType<DaoClaimHistoryItemType> = [
    {
      title: 'No',
      key: 'index',
      width: 60,
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      render: (address: string) => (
        <Space size={4}>
          <LinkAddress address={address} />
          <ButtonCopy title="Copy" text={address} icon={<CopyOutlined />} />
        </Space>
      ),
    },
    {
      // spec (5.10): USDC claimed + the PTO burned in the same column, sortable
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      render: (amount: number, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{formatNumber(amount || 0, 6)} USDC</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{record.tokenCount} PTOs</div>
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'claimedAt',
      key: 'claimedAt',
      sorter: true,
      render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '--'),
    },
    {
      // spec (5.10): icon opens the tx on the scan site in a new tab
      title: 'Tx',
      dataIndex: 'txHash',
      key: 'txHash',
      width: 60,
      align: 'center',
      render: (txHash?: string) =>
        txHash ? (
          <a href={renderAddressUrl(txHash, 'tx')} target="_blank" rel="noreferrer">
            <ExportOutlined />
          </a>
        ) : (
          '--'
        ),
    },
  ]

  const title = (data?.poolType && POOL_TYPE_TITLE[data.poolType]) || 'Claim History'

  return (
    <>
      <div className="dao-section-title">{title}</div>
      <Card>
        <Space style={{ marginBottom: 12 }}>
          <Input
            placeholder="Search by wallet address"
            maxLength={255}
            allowClear
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value)
              // clicking the clear "x" resets the applied filter immediately
              if (!e.target.value) {
                setPage(1)
                setKeyword('')
              }
            }}
            onPressEnter={onSearch}
            style={{ width: 280 }}
          />
          <Button type="primary" onClick={onSearch}>
            Search
          </Button>
          <span style={{ color: 'rgba(0,0,0,0.55)' }}>
            Showing <b>{data?.results?.length || 0}</b> of <b>{data?.totalClaims || 0}</b> claims
          </span>
        </Space>
        <Table
          columns={columns}
          dataSource={data?.results || []}
          rowKey={(record) => record.txHash}
          loading={loading}
          onChange={(_, __, sorter) => {
            // fires on page changes too - only reset when the sort itself changed
            const single = Array.isArray(sorter) ? sorter[0] : sorter
            const nextSort = single?.order
              ? `${String(single.columnKey)}:${single.order === 'ascend' ? 'asc' : 'desc'}`
              : undefined
            if (nextSort !== sort) {
              setPage(1)
              setSort(nextSort)
            }
          }}
          pagination={{
            current: page,
            pageSize: limit,
            total: data?.totalResults || 0,
            showSizeChanger: true,
            pageSizeOptions: DAO_PAGE_SIZE_OPTIONS,
            onChange: (nextPage, nextSize) => {
              setPage(nextSize !== limit ? 1 : nextPage)
              setLimit(nextSize)
            },
          }}
          locale={{ emptyText: 'No claims yet' }}
          scroll={{ x: 800 }}
        />
      </Card>
    </>
  )
}

export default ClaimHistoryCard
